import React, { useState } from 'react';
import { useTheme } from '../ThemeContext';
import { homeownerPost } from '../utils/homeownerApi';
import { useToast } from './Toast';
import { DELETE_PHRASE, exportFilename } from '../shared/homeownerAccount';
import { shareTextFile } from '../utils/nativeShare';
import { HomeownerPushSetting } from './AccountPushSwitch';

function saveJsonFile(filename, payload) {
  const text = JSON.stringify(payload, null, 2);
  return shareTextFile(filename, text, 'application/json');
}

function downloadMessage(filename, where) {
  if (where === 'shared') {
    return `Choose where to save ${filename}.`;
  }
  return `${filename} is downloading. On a computer it goes to Downloads. On iPhone, check Files.`;
}

function HomeownerAccount({ onDeleted, onLoggedOut }) {
  const { colors } = useTheme();
  const { showToast } = useToast();
  const [confirming, setConfirming] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const result = await homeownerPost('/.netlify/functions/homeowner-account', { action: 'export' });
      const filename = exportFilename();
      const where = await saveJsonFile(filename, result.data || result);
      showToast(downloadMessage(filename, where), 'success', 6000);
    } catch (error) {
      showToast(error.message || 'Could not download your data.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const logOutEverywhere = async () => {
    setBusy(true);
    try {
      await homeownerPost('/.netlify/functions/homeowner-account', { action: 'logout-all' });
      showToast('Signed out on every device.', 'success');
      if (onLoggedOut) await onLoggedOut();
    } catch (error) {
      showToast(error.message || 'Could not log out of all devices.', 'error');
      setBusy(false);
    }
  };

  const removeAccount = async () => {
    if (phrase !== DELETE_PHRASE) return;
    setBusy(true);
    try {
      await homeownerPost('/.netlify/functions/homeowner-account', {
        action: 'delete',
        confirm: DELETE_PHRASE,
      });
      showToast('Your account and data have been deleted.', 'success');
      if (onDeleted) await onDeleted();
    } catch (error) {
      showToast(error.message || 'Could not delete your account.', 'error');
      setBusy(false);
    }
  };

  const rowStyle = {
    width: '100%',
    padding: '1rem 1.25rem',
    border: 'none',
    background: 'transparent',
    color: colors.text,
    cursor: busy ? 'default' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '1rem',
    fontWeight: 500,
    fontFamily: 'inherit',
    minHeight: '56px',
    textAlign: 'left',
  };

  return (
    <section style={{ marginTop: '2rem' }}>
      <div style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: '1.25rem',
        overflow: 'hidden',
      }}>
        <HomeownerPushSetting />
        <div style={{ borderTop: `1px solid ${colors.border}` }}>
        <button type="button" className="app-menu-row" onClick={download} disabled={busy} style={rowStyle}>
          <span>Download my data</span>
          <span style={{ color: colors.textSecondary }} aria-hidden="true">›</span>
        </button>
        </div>
        <div style={{ borderTop: `1px solid ${colors.border}` }}>
          <button type="button" className="app-menu-row" onClick={logOutEverywhere} disabled={busy} style={rowStyle}>
            <span>Log out of all devices</span>
            <span style={{ color: colors.textSecondary }} aria-hidden="true">›</span>
          </button>
        </div>
        <div style={{ borderTop: `1px solid ${colors.border}` }}>
          {!confirming ? (
            <button
              type="button"
              className="app-menu-row"
              onClick={() => setConfirming(true)}
              disabled={busy}
              style={{ ...rowStyle, color: '#b42318' }}
            >
              <span>Delete my account</span>
              <span style={{ color: colors.textSecondary }} aria-hidden="true">›</span>
            </button>
          ) : (
            <div style={{ padding: '1rem 1.25rem' }}>
              <p style={{ margin: '0 0 0.75rem', color: colors.text, lineHeight: 1.45 }}>
                This permanently deletes your Pacarion account, your home records, photos, documents, and chats. It cannot be undone.
              </p>
              <label htmlFor="delete-account-confirm" style={{ display: 'block', marginBottom: '0.35rem', color: colors.textSecondary, fontSize: '0.9rem' }}>
                Type DELETE
              </label>
              <input
                id="delete-account-confirm"
                value={phrase}
                onChange={(event) => setPhrase(event.target.value)}
                autoComplete="off"
                autoCapitalize="characters"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '0.75rem',
                  borderRadius: '0.75rem',
                  border: `1px solid ${colors.border}`,
                  background: colors.surfaceAlt || colors.surface,
                  color: colors.text,
                  fontSize: '1rem',
                  marginBottom: '0.75rem',
                }}
              />
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={removeAccount}
                  disabled={busy || phrase !== DELETE_PHRASE}
                  style={{
                    flex: '1 1 140px',
                    minHeight: '48px',
                    border: 'none',
                    borderRadius: '0.75rem',
                    background: phrase === DELETE_PHRASE ? '#b42318' : colors.border,
                    color: 'white',
                    fontWeight: 650,
                    cursor: phrase === DELETE_PHRASE && !busy ? 'pointer' : 'default',
                  }}
                >
                  {busy ? 'Deleting…' : 'Delete my account'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirming(false);
                    setPhrase('');
                  }}
                  disabled={busy}
                  style={{
                    flex: '1 1 120px',
                    minHeight: '48px',
                    borderRadius: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    background: 'transparent',
                    color: colors.text,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default HomeownerAccount;
export { saveJsonFile, downloadMessage };
